import React, { useEffect, useMemo, useState } from 'react';
import { toDisplayDate } from '../utils/dateOnly';
import { formatIstDateTime, isSameIstDay } from '../utils/istTime';
import {
  formatTaskMinutes,
  groupTaskSessionsForUser,
  sessionDurationMinutesInRange,
} from '../utils/taskTime';
import './TaskBoard/TaskBoard.css';

function formatStopDisplay(startTime, stopTime, referenceNow = Date.now()) {
  if (stopTime) return formatIstDateTime(stopTime);
  if (!startTime) return '—';
  if (isSameIstDay(startTime, referenceNow)) {
    return <span className="text-success">Active</span>;
  }
  return '—';
}

function formatSessionTime(minutes) {
  const mins = Number(minutes) || 0;
  if (mins <= 0) return '00:00';
  return formatTaskMinutes(mins);
}

export default function UserTaskDetailModal({
  userName,
  taskName,
  startDate,
  endDate,
  records,
  onClose,
}) {
  const [now, setNow] = useState(() => Date.now());

  const sessions = useMemo(() => {
    const groups = groupTaskSessionsForUser(
      records,
      userName,
      startDate,
      endDate,
      taskName,
      now
    );
    if (groups.length === 0) return [];
    return groups[0].sessions;
  }, [records, userName, startDate, endDate, taskName, now]);

  const hasActiveSession = useMemo(
    () => sessions.some((session) => !session.stop && isSameIstDay(session.start, now)),
    [sessions, now]
  );

  useEffect(() => {
    if (!hasActiveSession) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [hasActiveSession]);

  const totalMinutes = useMemo(
    () => sessions.reduce(
      (sum, session) => sum + sessionDurationMinutesInRange(
        session.start,
        session.stop,
        startDate,
        endDate,
        now
      ),
      0
    ),
    [sessions, startDate, endDate, now]
  );

  if (!userName || !taskName) return null;

  return (
    <div
      className="task-reports-login-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-task-detail-modal-title"
      onClick={onClose}
    >
      <div
        className="task-reports-login-modal task-reports-task-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="task-reports-login-modal-header">
          <h4 id="user-task-detail-modal-title">{userName}</h4>
          <button
            type="button"
            className="task-reports-login-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <p className="task-reports-login-modal-range text-muted small mb-1">
          {toDisplayDate(startDate)} — {toDisplayDate(endDate)}
        </p>
        <p className="task-reports-login-modal-task-name mb-1">
          <strong>Task:</strong> {taskName}
        </p>
        <p className="task-reports-login-modal-task-time task-reports-total-time-highlight mb-3">
          <strong>Total Time:</strong> {formatTaskMinutes(totalMinutes)}
        </p>

        {sessions.length === 0 ? (
          <div className="text-center text-muted py-3">
            No sessions found for this task.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead>
                <tr>
                  <th>Start</th>
                  <th>End</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id}>
                    <td>{formatIstDateTime(session.start)}</td>
                    <td>{formatStopDisplay(session.start, session.stop, now)}</td>
                    <td>
                      {formatSessionTime(sessionDurationMinutesInRange(
                        session.start,
                        session.stop,
                        startDate,
                        endDate,
                        now
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
